import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { extractNestedAnswers } from "@/encounters/components/PostpartumEncounter/utils/postpartum";
import {
  PostpartumEncounterQuestionnaireURLs,
  PostpartumOutputsQuestionsLinkIds,
} from "@/encounters/constants/postpartum-encounter";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";

const OutputsDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          PostpartumEncounterQuestionnaireURLs.Output,
          encounterId!,
        );

        if (!foundAmendmentHistory) {
          setCurrentVersion(undefined);
          setPreviousVersion(undefined);
          return;
        }

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  // If no outputs data has ever been recorded
  if (!currentVersion) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Outputs</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No outputs data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  // If outputs data exists but no previous version to compare against,
  // create an empty previous version to reuse the existing diff logic
  const effectivePreviousVersion =
    previousVersion || ({ item: [] } as unknown as QuestionnaireResponse);

  // Utility function to map urination values to their display labels
  const mapUrinationValuesToLabels = (values: string[]): string => {
    if (!values.length) return "";

    const valueToLabelMap: Record<string, string> = {
      "no-burning": "No burning",
      "no-frequency-issues": "No frequency issues",
      "no-pain": "No pain",
      "no-urgency": "No urgency",
      "no-incontinence": "No incontinence",
      burning: "Burning",
      urgency: "Urgency",
      "frequency-issues": "Frequency issues",
      pain: "Pain",
      incontinence: "Incontinence",
    };

    return values.map((value) => valueToLabelMap[value] || value).join(", ");
  };

  // Extract values for all outputs fields using extractNestedAnswers
  const previousAnswers = extractNestedAnswers(effectivePreviousVersion);
  const currentAnswers = extractNestedAnswers(currentVersion);

  // Parse urine normal values (now arrays of booleans)
  const previousUrineNormalValues = (
    previousAnswers[PostpartumOutputsQuestionsLinkIds.UrineNormal] || []
  ).map((v) => v === "true");
  const currentUrineNormalValues = (
    currentAnswers[PostpartumOutputsQuestionsLinkIds.UrineNormal] || []
  ).map((v) => v === "true");

  const previousUrineNormalDetails =
    previousAnswers[PostpartumOutputsQuestionsLinkIds.UrineNormalDetails] || [];
  const currentUrineNormalDetails =
    currentAnswers[PostpartumOutputsQuestionsLinkIds.UrineNormalDetails] || [];

  const previousUrineNotNormalDetails =
    previousAnswers[PostpartumOutputsQuestionsLinkIds.UrineNotNormalDetails] || [];
  const currentUrineNotNormalDetails =
    currentAnswers[PostpartumOutputsQuestionsLinkIds.UrineNotNormalDetails] || [];

  // Parse bowel normal values (now arrays of booleans)
  const previousBowelNormalValues = (
    previousAnswers[PostpartumOutputsQuestionsLinkIds.BowelNormal] || []
  ).map((v) => v === "true");
  const currentBowelNormalValues = (
    currentAnswers[PostpartumOutputsQuestionsLinkIds.BowelNormal] || []
  ).map((v) => v === "true");

  const previousBowelNormalDetails =
    previousAnswers[PostpartumOutputsQuestionsLinkIds.BowelNormalDetails] || [];
  const currentBowelNormalDetails =
    currentAnswers[PostpartumOutputsQuestionsLinkIds.BowelNormalDetails] || [];

  const previousBowelNotNormalDetails =
    previousAnswers[PostpartumOutputsQuestionsLinkIds.BowelNotNormalDetails] || [];
  const currentBowelNotNormalDetails =
    currentAnswers[PostpartumOutputsQuestionsLinkIds.BowelNotNormalDetails] || [];

  const previousNumberOfTimes =
    previousAnswers[PostpartumOutputsQuestionsLinkIds.NumberOfTimes]?.[0] || "";
  const currentNumberOfTimes =
    currentAnswers[PostpartumOutputsQuestionsLinkIds.NumberOfTimes]?.[0] || "";

  const previousNumberOfDays =
    previousAnswers[PostpartumOutputsQuestionsLinkIds.NumberOfDays]?.[0] || "";
  const currentNumberOfDays =
    currentAnswers[PostpartumOutputsQuestionsLinkIds.NumberOfDays]?.[0] || "";

  const previousNotes = previousAnswers[PostpartumOutputsQuestionsLinkIds.Notes]?.join(", ") || "";
  const currentNotes = currentAnswers[PostpartumOutputsQuestionsLinkIds.Notes]?.join(", ") || "";

  // Helper function to get status text for multiselect
  const getStatusText = (normalValues: boolean[]): string => {
    if (normalValues.length === 0) return "";
    const hasNormal = normalValues.includes(true);
    const hasNotNormal = normalValues.includes(false);

    if (hasNormal && hasNotNormal) return "Normal, Not Normal";
    if (hasNormal) return "Normal";
    if (hasNotNormal) return "Not Normal";
    return "";
  };

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Outputs</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Urination Status</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={getStatusText(currentUrineNormalValues)}
                  previous={getStatusText(previousUrineNormalValues)}
                />
              </Stack>
              {previousUrineNormalValues.includes(true) ||
              currentUrineNormalValues.includes(true) ? (
                <Stack gap="0">
                  <TextWrapper fw={600}>Urination Details (Normal)</TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={mapUrinationValuesToLabels(currentUrineNormalDetails)}
                    previous={mapUrinationValuesToLabels(previousUrineNormalDetails)}
                  />
                </Stack>
              ) : null}
              {previousUrineNormalValues.includes(false) ||
              currentUrineNormalValues.includes(false) ? (
                <Stack gap="0">
                  <TextWrapper fw={600}>Urination Details (Not Normal)</TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={mapUrinationValuesToLabels(currentUrineNotNormalDetails)}
                    previous={mapUrinationValuesToLabels(previousUrineNotNormalDetails)}
                  />
                </Stack>
              ) : null}
              <Stack gap="0">
                <TextWrapper fw={600}>Bowel Status</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={getStatusText(currentBowelNormalValues)}
                  previous={getStatusText(previousBowelNormalValues)}
                />
              </Stack>
              {previousBowelNormalValues.includes(true) ||
              currentBowelNormalValues.includes(true) ? (
                <Stack gap="0">
                  <TextWrapper fw={600}>Bowel Details (Normal)</TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentBowelNormalDetails.join(", ")}
                    previous={previousBowelNormalDetails.join(", ")}
                  />
                </Stack>
              ) : null}
              {previousBowelNormalValues.includes(false) ||
              currentBowelNormalValues.includes(false) ? (
                <Stack gap="0">
                  <TextWrapper fw={600}>Bowel Details (Not Normal)</TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentBowelNotNormalDetails.join(", ")}
                    previous={previousBowelNotNormalDetails.join(", ")}
                  />
                </Stack>
              ) : null}
              {(previousNumberOfTimes ||
                previousNumberOfDays ||
                currentNumberOfTimes ||
                currentNumberOfDays) && (
                <Stack gap="0">
                  <TextWrapper fw={600}>Frequency</TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={`${currentNumberOfTimes || 0} times per ${currentNumberOfDays || 0} day(s)`}
                    previous={`${previousNumberOfTimes || 0} times per ${previousNumberOfDays || 0} day(s)`}
                  />
                </Stack>
              )}
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Urination Status</TextWrapper>
                <WordsDiff
                  current={getStatusText(currentUrineNormalValues)}
                  previous={getStatusText(previousUrineNormalValues)}
                />
              </Stack>
              {previousUrineNormalValues.includes(true) ||
              currentUrineNormalValues.includes(true) ? (
                <Stack gap="0">
                  <TextWrapper fw={600}>Urination Details (Normal)</TextWrapper>
                  <WordsDiff
                    current={mapUrinationValuesToLabels(currentUrineNormalDetails)}
                    previous={mapUrinationValuesToLabels(previousUrineNormalDetails)}
                  />
                </Stack>
              ) : null}
              {previousUrineNormalValues.includes(false) ||
              currentUrineNormalValues.includes(false) ? (
                <Stack gap="0">
                  <TextWrapper fw={600}>Urination Details (Not Normal)</TextWrapper>
                  <WordsDiff
                    current={mapUrinationValuesToLabels(currentUrineNotNormalDetails)}
                    previous={mapUrinationValuesToLabels(previousUrineNotNormalDetails)}
                  />
                </Stack>
              ) : null}
              <Stack gap="0">
                <TextWrapper fw={600}>Bowel Status</TextWrapper>
                <WordsDiff
                  current={getStatusText(currentBowelNormalValues)}
                  previous={getStatusText(previousBowelNormalValues)}
                />
              </Stack>
              {previousBowelNormalValues.includes(true) ||
              currentBowelNormalValues.includes(true) ? (
                <Stack gap="0">
                  <TextWrapper fw={600}>Bowel Details (Normal)</TextWrapper>
                  <WordsDiff
                    current={currentBowelNormalDetails.join(", ")}
                    previous={previousBowelNormalDetails.join(", ")}
                  />
                </Stack>
              ) : null}
              {previousBowelNormalValues.includes(false) ||
              currentBowelNormalValues.includes(false) ? (
                <Stack gap="0">
                  <TextWrapper fw={600}>Bowel Details (Not Normal)</TextWrapper>
                  <WordsDiff
                    current={currentBowelNotNormalDetails.join(", ")}
                    previous={previousBowelNotNormalDetails.join(", ")}
                  />
                </Stack>
              ) : null}
              {(previousNumberOfTimes ||
                previousNumberOfDays ||
                currentNumberOfTimes ||
                currentNumberOfDays) && (
                <Stack gap="0">
                  <TextWrapper fw={600}>Frequency</TextWrapper>
                  <WordsDiff
                    current={`${currentNumberOfTimes || 0} times per ${currentNumberOfDays || 0} day(s)`}
                    previous={`${previousNumberOfTimes || 0} times per ${previousNumberOfDays || 0} day(s)`}
                  />
                </Stack>
              )}
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default OutputsDiff;
